"use client";

import { useCallback, useMemo, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { TopSellersFilterState } from "@/features/topsellers/types";
import {
  mergeFilterParamsIntoSearch,
  parseFiltersFromSearchParams,
} from "@/features/topsellers/utils";

/**
 * Filter state derived from the URL, applied via a REAL Next navigation.
 *
 * SteamDB-style instant back: `setFilters` calls `router.replace` (not
 * `history.replaceState`) so Next records the navigation and caches the
 * filtered RSC per-URL. Pressing Back then restores the filtered list
 * instantly from the Router Cache — and reload / shareable links hit the
 * SSR-filtered render (pages parse the same filters server-side).
 *
 * Why this doesn't bring back the old "eternal skeleton": the navigation is
 * wrapped in `startTransition`, so React keeps the CURRENT list visible while
 * the filtered route re-renders, then swaps — no Suspense fallback flash. (The
 * earlier replaceState hack existed precisely to dodge that flash; the
 * transition is the correct fix, and it's what makes Back instant.)
 *
 * `filters` is read reactively from `useSearchParams`, so it updates on apply
 * AND on browser back/forward without any manual popstate listener.
 *
 * `_persistKey` is accepted for source compatibility with the old
 * usePersistedFilters signature; the URL is the single source of truth.
 */
export function useUrlFilters(_persistKey?: string) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const filters = useMemo(
    () => parseFiltersFromSearchParams(searchParams),
    [searchParams],
  );

  const [, startTransition] = useTransition();

  const setFilters = useCallback(
    (
      updater:
        | TopSellersFilterState
        | ((prev: TopSellersFilterState) => TopSellersFilterState),
    ) => {
      const prev = parseFiltersFromSearchParams(searchParams);
      const next =
        typeof updater === "function"
          ? (updater as (p: TopSellersFilterState) => TopSellersFilterState)(prev)
          : updater;

      // Any filter change resets pagination to page 1 (the result set changed).
      const existing = new URLSearchParams(searchParams.toString());
      existing.delete("page");
      const merged = mergeFilterParamsIntoSearch(existing, next);
      const url = merged ? `${pathname}?${merged}` : pathname;

      // Real Next navigation → cached per-URL (instant Back). Transition keeps
      // the current list on screen during the filtered re-render (no skeleton).
      startTransition(() => {
        router.replace(url, { scroll: false });
      });
    },
    [router, pathname, searchParams],
  );

  return [filters, setFilters] as const;
}
